import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import {
  Search as SearchIcon,
  GridView as GridViewIcon,
  List as ListIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import ArticlesDashboardStyles from "./ArticlesDashboard.module.scss";
import Typography from "../../components/Typography/Typography";
import Button from "../../components/Button/Button";
import WhiteGeminiSvg from "../../assets/images/white_gemini_small_icon.svg";
import PencilDraftSvg from "../../assets/images/pencil_draft_icon.svg";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import {
  COMMON_ACTIONS,
  createArticleSessionThunk,
  fetchArticleListThunk,
  setSelectedArticleThunk,
} from "../../store/thunks/common.thunk";
import dayjs from "dayjs";
import EmptyArticlesSvg from "../../assets/images/empty_chat_icon.svg";

const ArticlesDashboard: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const articles = useAppSelector(
    (store) => store?.rootReducer?.commonReducer?.articleList
  );

  const profileData = useAppSelector(
    (store) => store?.rootReducer?.userInfoReducer?.profileData?.data
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleCreateNewClick = async () => {
    const response = await dispatch(createArticleSessionThunk());
    if (response?.session_id) {
      navigate(`/generate-article/${response.session_id}`);
    }
  };

  useEffect(() => {
    dispatch(fetchArticleListThunk());
  }, [dispatch]);

  const filteredArticles = (articles || [])
    .filter((a: any) => a.title && a.title.trim() !== "")
    .filter((a: any) =>
      a.title.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

  const formatDate = (isoDate: string) => {
    return dayjs(isoDate).format("MM/DD/YYYY");
  };

  const handleCardClick = (article: any) => {
    dispatch(setSelectedArticleThunk(article));
    dispatch({ type: COMMON_ACTIONS.SET_SESSION_ID, payload: article.session });
    navigate(`/generate-article/${article.session}`);
  };

  return (
    <div className={ArticlesDashboardStyles.containerWrapper}>
      <Box className={ArticlesDashboardStyles.container}>
        {/* Header Section */}
        <Box className={ArticlesDashboardStyles.header}>
          <Typography
            className={ArticlesDashboardStyles.welcomeText}
            fontWeight={400}
            fontSize={24}
          >
            Welcome,{" "}
            <span className={ArticlesDashboardStyles.userName}>
              {profileData?.given_name}
            </span>
          </Typography>

          <Button
            variant="contained"
            startIcon={<img src={WhiteGeminiSvg} alt="" />}
            className={ArticlesDashboardStyles.createButton}
            onClick={() => handleCreateNewClick()}
          >
            Create New Article
          </Button>
        </Box>

        {/* Search and View Controls */}
        <Box className={ArticlesDashboardStyles.controls}>
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            className={ArticlesDashboardStyles.searchField}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FCFCFD",
                borderRadius: "24px",
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&:hover fieldset": {
                  borderColor: "#3498db",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#3498db",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#7f8c8d",
                opacity: 1,
              },
            }}
          />

          <Box className={ArticlesDashboardStyles.viewControls}>
            <IconButton className={ArticlesDashboardStyles.viewButton}>
              <GridViewIcon />
            </IconButton>
            <IconButton className={ArticlesDashboardStyles.viewButton}>
              <ListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <Box
            className={ArticlesDashboardStyles.emptyStateWrapper}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="50vh"
            width="100%"
          >
            <img
              src={EmptyArticlesSvg}
              alt="No articles"
              style={{ width: 180, marginBottom: 8 }}
            />
            <Typography
              fontWeight={400}
              fontSize={14}
              color="#7f8c8d"
              marginBottom={3}
              textAlign="center"
              maxWidth={320}
            >
              You haven’t created any articles yet. Click “Create New Article”
              button to get started!
            </Typography>
          </Box>
        ) : (
          <Grid
            container
            spacing={3}
            className={ArticlesDashboardStyles.articlesGrid}
          >
            {filteredArticles.map((article: any) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Card
                  className={ArticlesDashboardStyles.articleCard}
                  onClick={() => handleCardClick(article)}
                  style={{ cursor: "pointer" }}
                >
                  <CardContent className={ArticlesDashboardStyles.cardContent}>
                    <Box className={ArticlesDashboardStyles.cardHeader}>
                      <Typography
                        className={ArticlesDashboardStyles.articleTitle}
                        fontWeight={600}
                        fontSize={14}
                        color="#282B31"
                      >
                        {article.display_title}
                      </Typography>
                    </Box>
                    <Box className={ArticlesDashboardStyles.cardFooter}>
                      <div className={ArticlesDashboardStyles.dateBadge}>
                        <Typography
                          variant="body2"
                          className={ArticlesDashboardStyles.articleDate}
                        >
                          {formatDate(article.updated_at)}
                        </Typography>
                        {article.status === "published" && (
                          <CheckCircleIcon
                            className={ArticlesDashboardStyles.publishedIcon}
                          />
                        )}
                      </div>
                      {article.status === "draft" && (
                        <div className={ArticlesDashboardStyles.draftChip}>
                          <img src={PencilDraftSvg} alt="" />
                          <Typography
                            color="#282B31"
                            fontWeight={400}
                            fontSize={10}
                          >
                            Draft
                          </Typography>
                        </div>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </div>
  );
};

export default ArticlesDashboard;
